import React from "react";
import { Building, Car, DollarSign, Fuel, Wrench, FileText, Truck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { VehicleSummary, VehicleDetailData } from "@/types/fleet-reports.v2";
import VehicleDetailSection from "./VehicleDetailSection";

interface CostCenter {
  id: string;
  name: string;
  code?: string | null;
}

interface CostCenterGroup {
  costCenter: CostCenter;
  vehicles: VehicleSummary[];
  totals: {
    totalCost: number;
    fuelCost: number;
    maintenanceCost: number;
    tiresCost: number;
    expensesCost: number;
    documentsCost: number;
    tripsCost: number;
    totalKm: number;
  };
}

interface Props {
  costCenterGroups: CostCenterGroup[];
  allVehiclesDetails: Map<string, VehicleDetailData>;
  isLoadingDetails: boolean;
  period: { from: Date; to: Date };
  limitPerCategory?: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value);
}

export default function CostCenterReportWrapper({
  costCenterGroups,
  allVehiclesDetails,
  isLoadingDetails,
  period,
  limitPerCategory = 500
}: Props) {
  if (costCenterGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Building className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg">Selecione pelo menos um centro de custo nos filtros</p>
      </div>
    );
  }

  // Calcular totais gerais
  const grandTotals = costCenterGroups.reduce(
    (acc, group) => ({
      totalCost: acc.totalCost + group.totals.totalCost,
      fuelCost: acc.fuelCost + group.totals.fuelCost,
      maintenanceCost: acc.maintenanceCost + group.totals.maintenanceCost,
      tiresCost: acc.tiresCost + group.totals.tiresCost,
      expensesCost: acc.expensesCost + group.totals.expensesCost,
      documentsCost: acc.documentsCost + group.totals.documentsCost,
      tripsCost: acc.tripsCost + group.totals.tripsCost,
      totalKm: acc.totalKm + group.totals.totalKm,
      vehicleCount: acc.vehicleCount + group.vehicles.length
    }),
    { totalCost: 0, fuelCost: 0, maintenanceCost: 0, tiresCost: 0, expensesCost: 0, documentsCost: 0, tripsCost: 0, totalKm: 0, vehicleCount: 0 }
  );

  return (
    <div className="space-y-8 print:space-y-4">
      {/* Resumo Executivo Geral */}
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-background">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Building className="h-6 w-6 text-primary" />
            Relatório por Centro de Custo
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Período: {period.from.toLocaleDateString('pt-BR')} a {period.to.toLocaleDateString('pt-BR')}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-background rounded-lg border">
              <p className="text-2xl font-bold text-primary">{costCenterGroups.length}</p>
              <p className="text-xs text-muted-foreground">Centros de Custo</p>
            </div>
            <div className="text-center p-3 bg-background rounded-lg border">
              <p className="text-2xl font-bold text-blue-600">{grandTotals.vehicleCount}</p>
              <p className="text-xs text-muted-foreground">Veículos</p>
            </div>
            <div className="text-center p-3 bg-background rounded-lg border">
              <p className="text-2xl font-bold text-green-600">{formatNumber(grandTotals.totalKm)} km</p>
              <p className="text-xs text-muted-foreground">Km Total</p>
            </div>
            <div className="text-center p-3 bg-background rounded-lg border">
              <p className="text-2xl font-bold text-red-600">{formatCurrency(grandTotals.totalCost)}</p>
              <p className="text-xs text-muted-foreground">Custo Total</p>
            </div>
          </div>
          
          {/* Breakdown de custos */}
          <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
            <div className="p-2 bg-muted/30 rounded">
              <Fuel className="h-4 w-4 mx-auto text-amber-500 mb-1" />
              <p className="text-xs font-medium">{formatCurrency(grandTotals.fuelCost)}</p>
              <p className="text-[10px] text-muted-foreground">Combustível</p>
            </div>
            <div className="p-2 bg-muted/30 rounded">
              <Wrench className="h-4 w-4 mx-auto text-blue-500 mb-1" />
              <p className="text-xs font-medium">{formatCurrency(grandTotals.maintenanceCost)}</p>
              <p className="text-[10px] text-muted-foreground">Manutenção</p>
            </div>
            <div className="p-2 bg-muted/30 rounded">
              <Car className="h-4 w-4 mx-auto text-gray-500 mb-1" />
              <p className="text-xs font-medium">{formatCurrency(grandTotals.tiresCost)}</p>
              <p className="text-[10px] text-muted-foreground">Pneus</p>
            </div>
            <div className="p-2 bg-muted/30 rounded">
              <DollarSign className="h-4 w-4 mx-auto text-green-500 mb-1" />
              <p className="text-xs font-medium">{formatCurrency(grandTotals.expensesCost)}</p>
              <p className="text-[10px] text-muted-foreground">Despesas</p>
            </div>
            <div className="p-2 bg-muted/30 rounded">
              <FileText className="h-4 w-4 mx-auto text-purple-500 mb-1" />
              <p className="text-xs font-medium">{formatCurrency(grandTotals.documentsCost)}</p>
              <p className="text-[10px] text-muted-foreground">Documentos</p>
            </div>
            <div className="p-2 bg-muted/30 rounded">
              <Truck className="h-4 w-4 mx-auto text-teal-500 mb-1" />
              <p className="text-xs font-medium">{formatCurrency(grandTotals.tripsCost)}</p>
              <p className="text-[10px] text-muted-foreground">Viagens</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cada Centro de Custo */}
      {costCenterGroups.map((group, groupIndex) => (
        <div key={group.costCenter.id} className="space-y-4">
          {/* Header do Centro de Custo */}
          <Card className="border-l-4 border-l-primary">
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building className="h-5 w-5 text-primary" />
                  <div>
                    <CardTitle className="text-lg">
                      {group.costCenter.code && (
                        <Badge variant="outline" className="mr-2">{group.costCenter.code}</Badge>
                      )}
                      {group.costCenter.name}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {group.vehicles.length} veículo(s) • {formatNumber(group.totals.totalKm)} km • {formatCurrency(group.totals.totalCost)}
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
                <div className="p-2 bg-muted/30 rounded">
                  <p className="font-medium">{formatCurrency(group.totals.fuelCost)}</p>
                  <p className="text-muted-foreground">Combustível</p>
                </div>
                <div className="p-2 bg-muted/30 rounded">
                  <p className="font-medium">{formatCurrency(group.totals.maintenanceCost)}</p>
                  <p className="text-muted-foreground">Manutenção</p>
                </div>
                <div className="p-2 bg-muted/30 rounded">
                  <p className="font-medium">{formatCurrency(group.totals.tiresCost)}</p>
                  <p className="text-muted-foreground">Pneus</p>
                </div>
                <div className="p-2 bg-muted/30 rounded">
                  <p className="font-medium">{formatCurrency(group.totals.expensesCost)}</p>
                  <p className="text-muted-foreground">Despesas</p>
                </div>
                <div className="p-2 bg-muted/30 rounded">
                  <p className="font-medium">{formatCurrency(group.totals.documentsCost)}</p>
                  <p className="text-muted-foreground">Documentos</p>
                </div>
                <div className="p-2 bg-muted/30 rounded">
                  <p className="font-medium">{formatCurrency(group.totals.tripsCost)}</p>
                  <p className="text-muted-foreground">Viagens</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Veículos deste Centro de Custo */}
          {group.vehicles.map((vehicleSummary, vehicleIndex) => {
            const details = allVehiclesDetails.get(vehicleSummary.vehicle_id);
            const vehicleDetailData = details || {
              ...vehicleSummary,
              fuel: [],
              maintenance: [],
              maintPlans: [],
              tires: [],
              expenses: [],
              documents: [],
              trips: []
            };
            return (
              <VehicleDetailSection
                key={vehicleSummary.vehicle_id}
                rank={vehicleIndex + 1}
                vehicle={vehicleDetailData}
                summaryFallback={vehicleSummary}
                period={period}
                isLoading={isLoadingDetails && !details}
              />
            );
          })}

          {groupIndex < costCenterGroups.length - 1 && (
            <Separator className="my-6" />
          )}
        </div>
      ))}
    </div>
  );
}
